import { useEffect, useRef, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Bell, X } from 'lucide-react'
import { clsx } from 'clsx'

import { useMe, useLogout } from '@/queries/auth'
import { useRooms } from '@/queries/messages'
import { useNotificationStream, useUnreadNotificationsCount } from '@/queries/notifications'
import { NotificationsDrawer } from '@/components/notifications/NotificationsDrawer'
import type { Role } from '@shared/enums'

type NavItem = { path: string; label: string; roles?: Role[] }
type NavGroup = { title: string; roles?: Role[]; items: NavItem[] }

const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Genel',
    items: [
      { path: '/dashboard', label: 'Ana Sayfa' },
      { path: '/messages', label: 'Mesajlar' },
      { path: '/orders', label: 'Siparişler' },
      { path: '/company-settings', label: 'Firma ayarları', roles: ['BUYER', 'SUPPLIER'] },
    ],
  },
  {
    title: 'Alıcı',
    roles: ['BUYER'],
    items: [
      { path: '/purchase-lists', label: 'Taleplerim' },
      { path: '/purchase-lists/new', label: 'Yeni Talep' },
      { path: '/catalogs', label: 'Kataloglar' },
    ],
  },
  {
    title: 'Tedarikçi',
    roles: ['SUPPLIER'],
    items: [
      { path: '/discover', label: 'Açık Talepler' },
      { path: '/my-offers', label: 'Tekliflerim' },
      { path: '/subscriptions', label: 'Abonelik' },
    ],
  },
  {
    title: 'Yönetici',
    roles: ['ADMIN'],
    items: [
      { path: '/admin/subscriptions', label: 'Abonelik Yönetimi' },
      { path: '/admin/users', label: 'Kullanıcılar' },
      { path: '/admin/companies', label: 'Şirketler' },
      { path: '/admin/purchase-lists', label: 'Açık Talepler' },
      { path: '/admin/offers', label: 'Tüm Teklifler' },
      { path: '/admin/categories', label: 'Kategoriler' },
      { path: '/admin/product-imports', label: 'Global Ürün Kataloğu' },
      { path: '/admin/reviews', label: 'Yorum Moderasyonu' },
      { path: '/admin/contact-submissions', label: 'İletişim Talepleri' },
      { path: '/admin/audit-logs', label: 'Audit Kayıtları' },
    ],
  },
]

const ROLE_LABEL: Record<Role, string> = {
  ADMIN: 'Yönetici',
  BUYER: 'Alıcı',
  SUPPLIER: 'Tedarikçi',
}

function isItemVisible(item: NavItem, role: Role | undefined): boolean {
  if (!item.roles) return true
  return role !== undefined && item.roles.includes(role)
}

function isGroupVisible(group: NavGroup, role: Role | undefined): boolean {
  // Boş grupları (örn. ADMIN henüz yok) role eşleşiyorsa da gizle
  if (group.items.length === 0) return false
  if (!group.roles) return true
  return role !== undefined && group.roles.includes(role)
}

type SidebarProps = {
  mobileOpen?: boolean
  onMobileClose?: () => void
}

export const Sidebar = ({ mobileOpen = false, onMobileClose }: SidebarProps) => {
  const me = useMe()
  const logout = useLogout()
  const roomsQuery = useRooms()
  const unreadNotifications = useUnreadNotificationsCount()
  useNotificationStream()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (mobileOpen) closeButtonRef.current?.focus()
  }, [mobileOpen])

  const user = me.data?.user
  const role = user?.role

  const visibleGroups = NAV_GROUPS.filter((g) => isGroupVisible(g, role))

  // Tüm odalardaki toplam okunmamış mesaj sayısı (badge için).
  // rooms.items dizi olabilir; useRooms hook'unun döndüğü yapıya göre erişim.
  const rooms = roomsQuery.data
  const totalUnread = rooms?.reduce((sum, r) => sum + (r.unreadCount ?? 0), 0) ?? 0

  const notificationCount = unreadNotifications.data ?? 0

  const handleLogout = async () => {
    await logout.mutateAsync()
    qc.clear()
    navigate('/login', { replace: true })
  }

  return (
    <aside
      id="app-sidebar"
      aria-label="Ana menü"
      className={clsx(
        'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-gray-200 bg-white transition-transform md:static md:translate-x-0',
        mobileOpen ? 'visible translate-x-0' : 'invisible -translate-x-full md:visible',
      )}
    >
      <div className="flex h-16 items-center justify-between border-b border-gray-200 px-4">
        <span className="text-xl font-bold text-brand-600">TedarikSen</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setNotificationsOpen(true)}
            className="relative rounded-md p-1.5 text-gray-600 hover:bg-gray-100"
            aria-label={`Bildirimler${notificationCount > 0 ? `, ${notificationCount} okunmamış` : ''}`}
          >
            <Bell className="h-5 w-5" />
            {notificationCount > 0 && (
              <span
                aria-hidden="true"
                className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white"
              >
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
            )}
          </button>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onMobileClose}
            className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100 md:hidden"
            aria-label="Menüyü kapat"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto p-4">
        {visibleGroups.map((group) => (
          <div key={group.title} className="mb-6 last:mb-0">
            <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
              {group.title}
            </p>
            <div className="space-y-1">
              {group.items
                .filter((item) => isItemVisible(item, role))
                .map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/dashboard'}
                    onClick={onMobileClose}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive ? 'bg-brand-50 text-brand-700' : 'text-gray-700 hover:bg-gray-100',
                      )
                    }
                  >
                    <span>{item.label}</span>
                    {item.path === '/messages' && totalUnread > 0 && (
                      <span
                        className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-semibold text-white"
                        aria-label={`${totalUnread} okunmamış mesaj`}
                      >
                        {totalUnread > 99 ? '99+' : totalUnread}
                      </span>
                    )}
                  </NavLink>
                ))}
            </div>
          </div>
        ))}
      </nav>

      {user && (
        <div className="border-t border-gray-200 p-4">
          <div className="mb-3">
            <p className="text-sm font-medium text-gray-900">{user.fullName}</p>
            <p className="text-xs text-gray-500 truncate">{user.email}</p>
            <span className="mt-1 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
              {ROLE_LABEL[user.role]}
            </span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            disabled={logout.isPending}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {logout.isPending ? 'Çıkış yapılıyor...' : 'Çıkış Yap'}
          </button>
        </div>
      )}

      <NotificationsDrawer open={notificationsOpen} onClose={() => setNotificationsOpen(false)} />
    </aside>
  )
}
